import React from 'react';
import { useEffect, useRef } from 'react';
import * as THREE from 'three';

export default function FogEffectCanvas({ snatchName }: { snatchName: boolean }) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.OrthographicCamera | null>(null);
  const meshRef = useRef<THREE.Mesh | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    const width = containerRef.current.clientWidth;
    const height = containerRef.current.clientHeight;

    // Renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(window.devicePixelRatio);
    containerRef.current.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    const programs = renderer.info.programs;
    console.log(programs);

    // Scene
    const scene = new THREE.Scene();
    sceneRef.current = scene;

    // Camera
    const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0, 1);
    cameraRef.current = camera;

    // Plane
    const geometry = new THREE.PlaneGeometry(2, 2);

    // Shader
    const material = new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uResolution: { value: new THREE.Vector2(width, height) },
      },
      vertexShader: `
        varying vec2 vUv;
        void main() {
          vUv = uv;
          gl_Position = vec4(position, 1.0);
        }
      `,
      fragmentShader: `precision highp float;

varying vec2 vUv;
uniform float uTime;
uniform vec2 uResolution;

// Simplex Noise (IQ's 2D version)
float hash(vec2 p) {
  return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123);
}
float noise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(
    mix(hash(i + vec2(0.0,0.0)), hash(i + vec2(1.0,0.0)), u.x),
    mix(hash(i + vec2(0.0,1.0)), hash(i + vec2(1.0,1.0)), u.x),
    u.y
  );
}

void main() {
  vec2 uv = vUv * uResolution / min(uResolution.x, uResolution.y);
  // Animate noise coordinates
  float t = ceil(uTime) * 0.9999;
  float n = 0.0;
  float freq = 1.0;
  float amp = 0.5;

  for (int i = 0; i < 5; i++) {
    n += noise(uv * freq + uTime) * amp;
    freq *= 2.0;
    amp *= 0.5;
  }

  n = smoothstep(0.3, 0.7, n);

  vec3 fogColor = mix(vec3(0.1), vec3(0.4, 0.3, 0.2), n);
  gl_FragColor = vec4(fogColor, 0.85);
}
`,
      transparent: true,
    });

    const mesh = new THREE.Mesh(geometry, material);
    scene.add(mesh);
    meshRef.current = mesh;

    // Animation loop
    let animationFrameId: number;
    const clock = new THREE.Clock();

    const render = () => {
      const delta = clock.getDelta();

      const mesh = meshRef.current!;
      const renderer = rendererRef.current!;
      const container = containerRef.current!;

      const w = container.clientWidth;
      const h = container.clientHeight;

      const mat = mesh.material as THREE.ShaderMaterial;
      mat.uniforms.uTime.value += delta;
      mat.uniforms.uResolution.value.set(w, h);

      renderer.render(scene, camera);
      animationFrameId = requestAnimationFrame(render);
    };

    render();

    // Handle resize
    const handleResize = () => {
      if (!containerRef.current || !rendererRef.current || !meshRef.current) return;
      const w = containerRef.current.clientWidth;
      const h = containerRef.current.clientHeight;
      rendererRef.current.setSize(w, h);

      const mat = meshRef.current.material as THREE.ShaderMaterial;
      mat.uniforms.uResolution.value = new THREE.Vector2(w, h);
    };
    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', handleResize);
      renderer.dispose();
    };
  }, []);

  return (
    <div id='glslbg'
      ref={containerRef}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: -1,
        pointerEvents: 'none',
        animation: `${snatchName ? "4s dive-out-bg 2s forwards" : ""}`
      }}
    />
  );
};
